import { Icons, SingleCombobox } from '@commenda/kakashi';
import { toTitleCaseSentence } from '@commenda/utils';
import type {
  IIndirectTaxRegistrationCondition,
  IIndirectTaxRegistrationConditionOption,
} from '@commenda/api/indirect-tax/registrations/types';

interface IndirectTaxConditionsStepProps {
  prerequisiteCondition: IIndirectTaxRegistrationCondition | null;
  memberCountryCondition: IIndirectTaxRegistrationCondition | null;
  availablePrerequisites: IIndirectTaxRegistrationConditionOption[];
  selectedPrerequisiteRegistrationTaxTypeId: string;
  onPrerequisiteChange: (registrationTaxTypeId: string) => void;
  selectedMemberCountryRegistrationTaxTypeId: string;
  onMemberCountryChange: (registrationTaxTypeId: string) => void;
  isEditMode?: boolean;
}

export function IndirectTaxConditionsStep({
  prerequisiteCondition,
  memberCountryCondition,
  availablePrerequisites,
  selectedPrerequisiteRegistrationTaxTypeId,
  onPrerequisiteChange,
  selectedMemberCountryRegistrationTaxTypeId,
  onMemberCountryChange,
  isEditMode = false,
}: IndirectTaxConditionsStepProps) {
  return (
    <div className="flex flex-col gap-3xl">
      {prerequisiteCondition && (
        <div className="flex flex-col gap-xl">
          <div className="flex flex-col gap-xs">
            <p className="txt-medium-bold text-text-primary">
              Prerequisite registration
            </p>
            <p className="txt-small-regular text-text-tertiary">
              This registration requires an existing domestic registration in
              one of the following jurisdictions. Select the country where you
              have an active registration.
            </p>
          </div>

          {availablePrerequisites.length > 0 || isEditMode ? (
            <div className="flex flex-col gap-xs">
              <p className="txt-small-medium text-text-secondary">
                {isEditMode ? 'Linked registration' : 'Select your existing registration'}
                {!isEditMode && (
                  <span className="text-utility-violet-600 ml-0.5">*</span>
                )}
              </p>
              <SingleCombobox
                value={selectedPrerequisiteRegistrationTaxTypeId}
                setValue={onPrerequisiteChange}
                options={(isEditMode ? prerequisiteCondition?.registrationOptions ?? [] : availablePrerequisites).map((opt) => ({
                  value: opt.registrationTaxTypeId,
                  label: toTitleCaseSentence(opt.registrationName),
                }))}
                placeholder="Select registration"
                _className="txt-medium-regular text-text-secondary h-11 px-3"
                canClearSelection={false}
                disabled={isEditMode}
              />
            </div>
          ) : (
            <div className="flex items-start gap-sm p-xl rounded-xl bg-utility-warning-50 border border-utility-warning-200">
              <Icons.AlertTriangle className="size-5 text-utility-warning-600 shrink-0 mt-0.5" />
              <div className="flex flex-col gap-xs">
                <p className="txt-small-bold text-text-primary">
                  Prerequisite not met
                </p>
                <p className="txt-small-regular text-text-secondary">
                  You need an existing domestic registration in an EU member
                  state before you can create this registration. Please create
                  a country-level VAT registration first.
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {memberCountryCondition && (
        <div className="flex flex-col gap-xl">
          <div className="flex flex-col gap-xs">
            <p className="txt-medium-bold text-text-primary">
              Member state to register through
            </p>
            <p className="txt-small-regular text-text-tertiary">
              Select the EU member state through which you want to file this
              registration. Your portal credentials will be linked to this
              country.
            </p>
          </div>

          <div className="flex flex-col gap-xs">
            <p className="txt-small-medium text-text-secondary">
              {isEditMode ? 'Registered through' : 'Select member state'}
              {!isEditMode && (
                <span className="text-utility-violet-600 ml-0.5">*</span>
              )}
            </p>
            <SingleCombobox
              value={selectedMemberCountryRegistrationTaxTypeId}
              setValue={onMemberCountryChange}
              options={memberCountryCondition.registrationOptions.map((opt) => ({
                value: opt.registrationTaxTypeId,
                label: toTitleCaseSentence(opt.registrationName),
              }))}
              placeholder="Select member state"
              _className="txt-medium-regular text-text-secondary h-11 px-3"
              canClearSelection={false}
              disabled={isEditMode}
            />
          </div>
        </div>
      )}
    </div>
  );
}
